/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/pt';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DynamicFieldTypes } from '../../entities/dynamic-field-type';
import { ReactComponent as EditIcon } from '../../assets/ic_edit.svg';
import { ReactComponent as DeleteIcon } from '../../assets/ic_delete.svg';

DynamicFieldValueComponent.propTypes = {
  value: PropTypes.object,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  titleOnly: PropTypes.bool,
};

/*
 * Renders a single dynamic field (title + type-specific value display)
 * with edit / delete affordances. Used inside Resource and Flow forms
 * after a field is added via DynamicFieldSelectorDialog. Pure shadcn —
 * Input/Checkbox/DatePicker for the value preview, ghost icon Buttons
 * for the actions.
 */
export default function DynamicFieldValueComponent({ value, onEdit, onDelete, titleOnly = false }) {
  const { t } = useTranslation();

  const [fieldTitle] = useState(value && value.title);
  const [dataType] = useState(value && value.dataType);
  const [fieldValue] = useState(value && value.value);

  function translationForDataType() {
    switch (dataType) {
      case DynamicFieldTypes.BOOLEAN:
        return t('components.dynamic-field-selector.field-data-type.boolean');
      case DynamicFieldTypes.DATE:
        return t('components.dynamic-field-selector.field-data-type.date');
      case DynamicFieldTypes.NUMBER:
        return t('components.dynamic-field-selector.field-data-type.number');
      case DynamicFieldTypes.TEXT:
        return t('components.dynamic-field-selector.field-data-type.text');
      default:
        return '';
    }
  }

  const locale = (navigator.language || navigator.userLanguage).substring(0, 2);
  const dateFormat = locale === 'en' ? 'MM/DD/YYYY' : 'DD/MM/YYYY';

  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        {!titleOnly ? (
          dataType === DynamicFieldTypes.BOOLEAN ? (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={Boolean(fieldValue)} disabled />
              <span>{fieldTitle}</span>
            </label>
          ) : dataType === DynamicFieldTypes.DATE ? (
            <DatePicker
              label={fieldTitle}
              value={fieldValue ? dayjs(fieldValue) : null}
              onChange={() => {}}
              format={dateFormat}
              locale={locale}
              disabled
            />
          ) : (
            <div className="space-y-1">
              <Label className="text-sm font-medium after:ml-0.5 after:text-destructive after:content-['*']">
                {fieldTitle}
              </Label>
              <Input
                defaultValue={fieldValue ?? ''}
                readOnly
                required
              />
            </div>
          )
        ) : (
          <p className="text-sm">
            <span className="font-medium">{fieldTitle}</span>
            <span className="ml-1 text-muted-foreground">({translationForDataType()})</span>
          </p>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => onEdit(value)}
        aria-label={t('common.edit', 'Edit')}
      >
        <EditIcon />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => onDelete(value)}
        aria-label={t('common.delete', 'Delete')}
      >
        <DeleteIcon />
      </Button>
    </div>
  );
}
